import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';
import type {
  CreateFlowInstanceRequest,
  FlowInstance,
  FlowInstanceStepItem,
  OnboardingBranding,
} from './types';

/**
 * `item_config` is a jsonb column the Tenant API has historically stored as a
 * JSON *string* (double-encoded). The backend parses it defensively everywhere;
 * normalise it here too so the runner reads a plain object (input_type,
 * validation_rules, is_required, …) instead of a string it can't render.
 */
function parseItemConfig(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  }
  return value as Record<string, unknown> | undefined;
}

function normalizeItem(item: FlowInstanceStepItem): FlowInstanceStepItem {
  return {
    ...item,
    item_config: parseItemConfig(item.item_config),
    children: item.children?.map(normalizeItem),
  };
}

function normalizeInstance(instance: FlowInstance): FlowInstance {
  if (!instance?.steps) return instance;
  return {
    ...instance,
    steps: instance.steps.map((step) => ({
      ...step,
      items: step.items?.map(normalizeItem),
    })),
  };
}

/**
 * Onboarding API — canonical `/public/flow-instances/*` routes, mirroring
 * the Angular member-portal `OnboardingService`. Onboarding is a stateful
 * server flow: create an instance, value its items, complete steps, advance.
 */

export async function createFlowInstance(req: CreateFlowInstanceRequest): Promise<FlowInstance> {
  const { data } = await tenantApi.post('/public/flow-instances', req);
  return normalizeInstance(unwrap<FlowInstance>(data));
}

export async function getFlowInstance(instanceId: string): Promise<FlowInstance> {
  const { data } = await tenantApi.get(`/public/flow-instances/${instanceId}`);
  return normalizeInstance(unwrap<FlowInstance>(data));
}

export async function getOnboardingBranding(onboardingId: string): Promise<OnboardingBranding> {
  const { data } = await tenantApi.get(`/public/onboarding/${onboardingId}/branding`);
  return unwrap<OnboardingBranding>(data);
}

export async function updateItemValue(
  instanceId: string,
  itemId: string,
  value: unknown,
): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/items/${itemId}/value`, { value });
}

export async function executeItemAction(
  instanceId: string,
  itemId: string,
  parameters: Record<string, unknown> = {},
): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/items/${itemId}/execute`, {
    parameters,
  });
}

export async function completeStep(instanceId: string, stepId: string): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/steps/${stepId}/complete`);
}

export async function activateStep(instanceId: string, stepId: string): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/steps/${stepId}/activate`);
}

export async function moveToNextStep(instanceId: string): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/next-step`);
}

export async function moveToPreviousStep(instanceId: string): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/previous-step`);
}

export async function completeInstance(instanceId: string): Promise<void> {
  await tenantApi.put(`/public/flow-instances/${instanceId}/complete`);
}

/** Request an OTP for an `otpverify` action item. Returns the verify token. */
export async function requestFlowOtp(
  instanceId: string,
  itemId: string,
  destination?: string,
): Promise<{ token?: string; [key: string]: unknown }> {
  const { data } = await tenantApi.post(
    `/public/flow-instances/${instanceId}/items/${itemId}/otp/request`,
    { destination },
  );
  return unwrap<{ token?: string; [key: string]: unknown }>(data);
}

/** Verify the OTP code for an `otpverify` action item. */
export async function verifyFlowOtp(
  instanceId: string,
  itemId: string,
  token: string,
  code: string,
): Promise<void> {
  await tenantApi.post(`/public/flow-instances/${instanceId}/items/${itemId}/otp/verify`, {
    token,
    code,
  });
}

export async function uploadFlowFiles(instanceId: string, files: File[]): Promise<string[]> {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  const { data } = await tenantApi.post(`/public/flow-instances/${instanceId}/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const result = unwrap<{ files?: Array<{ url: string }> }>(data);
  return (result.files ?? []).map((f) => f.url);
}
